import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import React, { memo, useEffect, useState } from 'react';

import { ScenarioLayer, ScenarioLayerCreate, scenarioLayersApi } from '@/api/scenarioLayers';
import { CreateScenarioLayerModal } from './CreateScenarioLayerModal';

interface ScenarioLayersStepProps {
  scenarioId: string;
  onLayersChange?: (layers: ScenarioLayer[]) => void;
  onGoToWells?: () => void;
  nextLabel?: string;
}

export const ScenarioLayersStep: React.FC<ScenarioLayersStepProps> = memo(({
  scenarioId,
  onLayersChange,
  onGoToWells,
  nextLabel = 'Save and go to Reservoir configuration',
}) => {
  const [layers, setLayers] = useState<ScenarioLayer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [layerToDelete, setLayerToDelete] = useState<ScenarioLayer | null>(null);
  const [editingLayer, setEditingLayer] = useState<ScenarioLayer | null>(null);

  // Load scenario layers
  useEffect(() => {
    const loadScenarioLayers = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Loading scenario layers for scenarioId:', scenarioId);
        const scenarioLayers = await scenarioLayersApi.getScenarioLayers(scenarioId);
        console.log('Loaded scenario layers:', scenarioLayers);
        setLayers(scenarioLayers);
        onLayersChange?.(scenarioLayers);
      } catch (err) {
        console.error('Failed to load scenario layers:', err);
        setError('Failed to load scenario layers');
      } finally {
        setLoading(false);
      }
    };

    if (scenarioId) {
      loadScenarioLayers();
    }
  }, [scenarioId, onLayersChange]);

  const handleCreateLayer = async (layerData: ScenarioLayerCreate): Promise<ScenarioLayer> => {
    try {
      const created = await scenarioLayersApi.createScenarioLayer(scenarioId, layerData);
      // Reload layers from server to ensure we have the latest data
      const scenarioLayers = await scenarioLayersApi.getScenarioLayers(scenarioId);
      setLayers(scenarioLayers);
      onLayersChange?.(scenarioLayers);
      return created;
    } catch (err) {
      console.error('Failed to create scenario layer:', err);
      throw err; // Re-throw to let modal handle the error
    }
  };

  const handleDeleteLayerClick = (layer: ScenarioLayer) => {
    setLayerToDelete(layer);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!layerToDelete) return;

    try {
      await scenarioLayersApi.deleteScenarioLayer(layerToDelete.scenario_layer_id);
      // Reload layers from server to ensure we have the latest data
      const scenarioLayers = await scenarioLayersApi.getScenarioLayers(scenarioId);
      setLayers(scenarioLayers);
      onLayersChange?.(scenarioLayers);
      setDeleteDialogOpen(false);
      setLayerToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete scenario layer:', err);
      // ApiClient transforms errors to ApiError format with 'detail' field
      const errorMsg = err.detail || err.response?.data?.detail || err.message || 'Failed to delete scenario layer';
      setErrorMessage(errorMsg);
      setErrorDialogOpen(true);
      setDeleteDialogOpen(false);
      setLayerToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setLayerToDelete(null);
  };

  const handleUpdateLayer = async (scenarioLayerId: string, layerData: Partial<ScenarioLayerCreate>) => {
    try {
      await scenarioLayersApi.updateScenarioLayer(scenarioLayerId, layerData);
      // Reload layers from server to ensure we have the latest data
      const scenarioLayers = await scenarioLayersApi.getScenarioLayers(scenarioId);
      setLayers(scenarioLayers);
      onLayersChange?.(scenarioLayers);
    } catch (err) {
      console.error('Failed to update scenario layer:', err);
      throw err; // Re-throw to let modal handle the error
    }
  };

  const handleEditLayer = (layer: ScenarioLayer) => {
    setEditingLayer(layer);
    setIsCreateModalOpen(true);
  };

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
    setError(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setEditingLayer(null);
    setError(null);
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case 'oil':
        return 'success';
      case 'gas':
        return 'info';
      case 'oil_and_gas':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Scenario Reservoirs
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateModal}
        >
          Add Reservoir
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {console.log('Rendering layers:', layers, 'Length:', layers?.length)}
      {!layers || layers.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body2" color="textSecondary" align="center">
              No reservoirs added yet. Click "Add Reservoir" to create the first reservoir.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Properties</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {layers.filter(layer => layer).map((layer) => (
                <TableRow key={layer.scenario_layer_id}>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => handleEditLayer(layer)}
                    >
                      {layer.layer_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(layer.layer?.type || layer.layer_template?.type) && (
                      <Chip
                        label={layer.layer?.type || layer.layer_template?.type}
                        color={getTypeColor(layer.layer?.type || layer.layer_template?.type) as any}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {layer.ref_layer && (
                      <Typography variant="body2" color="primary">
                        Existing Reservoir: {layer.layer?.name}
                      </Typography>
                    )}
                    {layer.ref_layer_template && (
                      <Typography variant="body2" color="secondary">
                        Template: {layer.layer_template?.name}
                      </Typography>
                    )}
                    {!layer.ref_layer && !layer.ref_layer_template && (
                      <Typography variant="body2" color="textSecondary">
                        New Reservoir
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {layer.properties.length} properties
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteLayerClick(layer)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Action Buttons */}
      {layers && layers.length > 0 && (
        <Box sx={{
          mt: 4,
          px: 2,
          display: 'flex',
          gap: 2,
        }}>
          <Button
            variant="contained"
            onClick={onGoToWells}
            sx={{
              minWidth: 200,
            }}
          >
            {nextLabel}
          </Button>
        </Box>
      )}

      {/* Create/Edit Layer Modal */}
      <CreateScenarioLayerModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onLayerCreated={handleCreateLayer}
        onLayerUpdated={handleUpdateLayer}
        scenarioId={scenarioId}
        editingLayer={editingLayer}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Reservoir Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the reservoir "{layerToDelete?.layer_name}"?
            This action cannot be undone and will remove all associated properties.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title" sx={{ color: 'error.main' }}>
          Cannot Delete Reservoir
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="error-dialog-description">
            {errorMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogOpen(false)} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});
